import { Download, Clipboard, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import {
  type AnimalExportRow,
  type ExportProfile,
  ANIMAL_EXPORT_PROFILES,
  formatAnimalsAsTsv,
  formatAnimalsAsHtml,
  formatAnimalsAsCsv,
  downloadCsv,
} from "@/lib/animal-export";

interface AnimalExportButtonProps {
  animals: AnimalExportRow[];
  disabled?: boolean;
  profiles?: ExportProfile[];
}

export function AnimalExportButton({
  animals,
  disabled = false,
  profiles = ANIMAL_EXPORT_PROFILES,
}: AnimalExportButtonProps) {
  const { toast } = useToast();
  const count = animals.length;

  const handleCopy = async (profile: ExportProfile) => {
    const tsv = formatAnimalsAsTsv(animals, profile);
    const html = formatAnimalsAsHtml(animals, profile);
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([tsv], { type: "text/plain" }),
        }),
      ]);
      toast({
        title: `Copied ${count} animal${count !== 1 ? "s" : ""} to clipboard`,
        description: "Paste into Google Docs, Sheets, or any text editor.",
      });
    } catch {
      toast({
        title: "Copy failed",
        description: "Your browser blocked clipboard access. Try downloading instead.",
        variant: "destructive",
      });
    }
  };

  const handleDownload = (profile: ExportProfile) => {
    const csv = formatAnimalsAsCsv(animals, profile);
    const date = new Date().toISOString().slice(0, 10);
    const filename = `animals-${date}.csv`;
    downloadCsv(csv, filename);
    toast({
      title: `Downloading ${filename}`,
    });
  };

  const trigger = (
    <Button
      variant="outline"
      size="sm"
      disabled={disabled}
      data-testid="button-export-animals"
      className="gap-1.5"
    >
      <Download className="h-3.5 w-3.5" />
      Export · {count}
      <ChevronDown className="h-3 w-3 text-muted-foreground" />
    </Button>
  );

  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>{trigger}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p>Switch to List view to export</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {profiles.map((profile, idx) => (
          <div key={profile.id}>
            {idx > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={() => handleCopy(profile)}
              data-testid={`button-export-copy-${profile.id}`}
              className="gap-2"
            >
              <Clipboard className="h-3.5 w-3.5 text-muted-foreground" />
              Copy to clipboard
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleDownload(profile)}
              data-testid={`button-export-download-${profile.id}`}
              className="gap-2"
            >
              <Download className="h-3.5 w-3.5 text-muted-foreground" />
              Download CSV
            </DropdownMenuItem>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
